import type { RouterOutputs } from "@/lib/trpc";

/**
 * R2 — the ALLOW-LISTED print model + mapper. This is the print PRIVACY /
 * TRUST boundary: a printed report may be downloaded, forwarded, emailed, or
 * stored outside the app, so the print document is built ONLY from these
 * narrow presentation fields — never from lifecycle/mutation state, source
 * internals, or private data.
 *
 * `toPrintModel` copies EXACTLY the fields below out of a single selected
 * stored `ReportContent` (already server-sanitized) plus safe presentation
 * metadata. It structurally cannot carry: sourceSnapshotJson, sourceFingerprint,
 * generationMeta, rejectedReason, inputCycleOpenAt, canEditReportInputs,
 * currentDraftRevisionId, approvedRevisionId, any revisionNumber / revision id,
 * updatedBy, userId, model/provider name, lifecycle tokens, authority-role
 * metadata, or private priority/link/relevance data. The model contract test
 * pins that this file references none of them.
 */
type GetOut = RouterOutputs["weeklyTeamReports"]["get"];
type ReportContent = NonNullable<GetOut["approved"]>["content"];

export type PrintProgressStatus =
  | "on_track"
  | "needs_attention"
  | "blocked"
  | "completed_this_week";

/** One focused priority's HUMAN-declared progress (verbatim, never AI/receipt-derived). */
export interface PrintPriority {
  title: string;
  status: PrintProgressStatus;
  note?: string;
  blocker?: string;
  nextStep?: string;
  decisionRequired?: string;
}

/** Frozen legacy narrative — only used when a report has no per-priority declarations. */
export interface PrintLegacyProgress {
  status: PrintProgressStatus;
  summary: string;
  blocker?: string;
  nextStep?: string;
}

export interface WeeklyReportPrintModel {
  teamName: string;
  weekLabel: string;
  /** The ONLY lifecycle-adjacent field — a presentation label, not a token. */
  status: "approved" | "draft";
  /** Pre-formatted display date; approved reports only. No timestamps/ids. */
  approvedDate?: string;
  isAr: boolean;
  intro?: string;
  focus: { title: string; note?: string }[];
  priorities: PrintPriority[];
  legacyProgress?: PrintLegacyProgress;
  highlights: { headline: string; lines: string[] }[];
  directions: { title: string; importance: number }[];
}

/** Trim to `undefined` so empty optional fields are structurally absent. */
const clean = (v: string | null | undefined): string | undefined => {
  const t = (v ?? "").trim();
  return t.length > 0 ? t : undefined;
};

/**
 * Build the print model from the SELECTED report content (approved OR draft —
 * never both) and safe presentation metadata. The caller decides which single
 * revision's content is passed; this mapper never queries or combines both.
 */
export function toPrintModel(args: {
  content: ReportContent;
  status: "approved" | "draft";
  teamName: string;
  weekLabel: string;
  approvedDate?: string;
  isAr: boolean;
}): WeeklyReportPrintModel {
  const c = args.content;
  const priorities: PrintPriority[] = (c.declarations ?? []).map(d => ({
    title: d.teamPriorityTitle,
    status: d.status,
    note: clean(d.note),
    blocker: clean(d.blocker),
    nextStep: clean(d.nextStep),
    decisionRequired: clean(d.decisionRequired),
  }));
  // The legacy "Weekly progress" narrative is used ONLY for frozen historical
  // content with no per-priority declarations. When declarations exist they ARE
  // the weekly-progress section, and the legacy echo (screen "Additional weekly
  // context") is intentionally omitted from the portable document to avoid
  // duplicating status — matching the spec's fixed print section order.
  const legacyProgress: PrintLegacyProgress | undefined =
    priorities.length === 0 && c.progress
      ? {
          status: c.progress.status,
          summary: c.progress.summary,
          blocker: clean(c.progress.blocker),
          nextStep: clean(c.progress.nextStep),
        }
      : undefined;
  return {
    teamName: args.teamName,
    weekLabel: args.weekLabel,
    status: args.status,
    approvedDate: args.approvedDate,
    isAr: args.isAr,
    intro: clean(c.intro),
    focus: c.focus.map(f => ({ title: f.title, note: clean(f.note) })),
    priorities,
    legacyProgress,
    highlights: c.highlights.map(h => ({ headline: h.headline, lines: h.lines })),
    directions: c.directions.map(d => ({ title: d.title, importance: d.importance })),
  };
}
